import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { FiUserPlus, FiX, FiCheck, FiUsers } from "react-icons/fi";
import UserAvatar from "../UserAvatar";
import { followApi } from "../../../services/api/followApi";
import "./widgets.css";

const PeopleSuggestionsWidget = ({ suggestions: initialSuggestions }) => {
  const [localSuggestions, setLocalSuggestions] = useState(
    initialSuggestions || []
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreFromBackend, setHasMoreFromBackend] = useState(true);
  const [followedUsers, setFollowedUsers] = useState(new Set());

  const handleFollow = async (userId) => {
    try {
      await followApi.followUser(userId);
      setFollowedUsers((prev) => new Set([...prev, userId]));
    } catch (error) {
      alert("Failed to follow user. Please try again.");
    }
  };

  const handleDismiss = (userId) => {
    setLocalSuggestions((prev) => prev.filter((s) => s._id !== userId));
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Get connection reason text based on reason type
  const getConnectionReasonText = (person) => {
    switch (person.connectionReason) {
      case 'same_institution':
        return `Works at ${person.institution?.name || 'your institution'}`;
      case 'mutual_connections':
        return `${person.mutualCount || 0} mutual connections`;
      case 'shared_research_interests':
        return 'Shares your research interests';
      case 'shared_interests':
        return 'Has similar interests';
      case 'same_field':
        return `Also in ${person.field || 'your field'}`;
      case 'influential_researcher':
        return 'Influential researcher';
      default:
        return person.field || '';
    }
  };

  const loadMoreFromBackend = useCallback(async () => {
    if (loadingMore || !hasMoreFromBackend) return;

    setLoadingMore(true);
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:8000/api"
        }/collaborators/suggestions`,
        {
          params: { limit: 15 },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const newSuggestions = response.data.data?.suggestions || [];

      if (newSuggestions.length === 0) {
        setHasMoreFromBackend(false);
      } else {
        const existingIds = new Set(localSuggestions.map((s) => s._id));
        const uniqueNew = newSuggestions.filter((s) => !existingIds.has(s._id));

        if (uniqueNew.length > 0) {
          setLocalSuggestions((prev) => [...prev, ...uniqueNew]);
        } else {
          setHasMoreFromBackend(false);
        }
      }
    } catch (error) {
      setHasMoreFromBackend(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMoreFromBackend, localSuggestions]);

  const handleSeeMore = async () => {
    const nextIndex = currentIndex + 3;

    if (
      nextIndex + 3 >= localSuggestions.length &&
      hasMoreFromBackend &&
      !loadingMore
    ) {
      await loadMoreFromBackend();
    }

    if (nextIndex >= localSuggestions.length) {
      if (!hasMoreFromBackend) {
        const shuffled = shuffleArray(localSuggestions);
        setLocalSuggestions(shuffled);
        setCurrentIndex(0);
      } else {
        setCurrentIndex(0);
      }
    } else {
      setCurrentIndex(nextIndex);
    }
  };

  const visibleSuggestions = localSuggestions.slice(
    currentIndex,
    currentIndex + 3
  );
  const hasMore = localSuggestions.length > 3;

  if (!localSuggestions || localSuggestions.length === 0) {
    return (
      <motion.div
        className="cbx-widget cbx-widget--suggestions"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="cbx-widget__header">
          <h3 className="cbx-widget__title">
            <FiUserPlus className="cbx-widget__title-icon" />
            People You May Know
          </h3>
        </div>
        <div className="cbx-widget__empty">
          <FiUsers className="cbx-widget__empty-icon" />
          <p className="cbx-widget__empty-text">No suggestions available</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="cbx-widget cbx-widget--suggestions"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="cbx-widget__header">
        <h3 className="cbx-widget__title">
          <FiUserPlus className="cbx-widget__title-icon" />
          People You May Know
        </h3>
      </div>

      <div className="cbx-suggestions-list">
        <AnimatePresence mode="popLayout">
          {visibleSuggestions.map((person) => (
            <motion.div
              key={person._id}
              className="cbx-suggestion-item"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              <Link
                to={`/profile/${person._id}`}
                className="cbx-suggestion-avatar"
              >
                <UserAvatar user={person} size="medium" />
              </Link>

              <div className="cbx-suggestion-content">
                <div className="cbx-suggestion-header">
                  <Link
                    to={`/profile/${person._id}`}
                    className="cbx-suggestion-name"
                  >
                    {person.name}
                  </Link>
                </div>
                {/* Role + Institution (separate lines) */}
                <p className="cbx-suggestion-role">
                  {person.professionalRole || "Researcher"}
                </p>
                {person.institution?.verified && person.institution?.name && (
                  <p className="cbx-suggestion-institution">
                    {person.institution.name}
                  </p>
                )}
                
                {/* Connection Reason */}
                <div className="cbx-suggestion-meta">
                  <span className="cbx-suggestion-reason">
                    {getConnectionReasonText(person)}
                  </span>
                </div>
              </div>

              <div className="cbx-suggestion-actions">
                <motion.button
                  className={`cbx-follow-btn ${
                    followedUsers.has(person._id)
                      ? "cbx-follow-btn--following"
                      : ""
                  }`}
                  onClick={() => handleFollow(person._id)}
                  disabled={followedUsers.has(person._id)}
                  whileTap={{ scale: 0.95 }}
                >
                  {followedUsers.has(person._id) ? (
                    <>
                      <FiCheck style={{ marginRight: 4 }} />
                      Following
                    </>
                  ) : (
                    "Follow"
                  )}
                </motion.button>
                <button
                  onClick={() => handleDismiss(person._id)}
                  className="cbx-dismiss-btn"
                  title="Dismiss"
                >
                  <FiX />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {hasMore && (
        <button
          className="cbx-show-more-btn"
          onClick={handleSeeMore}
          disabled={loadingMore}
        >
          {loadingMore ? "Loading..." : "See More"}
        </button>
      )}
    </motion.div>
  );
};

export default PeopleSuggestionsWidget;
